"use client";

import * as X from "@/ui";

import "./styles/QuestionAnswer.scss";
import { FaqQuestion } from "@/providers/strapi/content/types/components/shared.component";
import ReactMarkdown from "react-markdown";


export type Question = {
  title: React.ReactNode;
  answer: React.ReactNode;
  url?: never;
} | {
  title: React.ReactNode;
  answer?: never;
  url: string;
};


export function Questions(props: X.PageProps<{
  className?: string;
  questions: FaqQuestion[];
}>) {
  const [openQuestionIndex, setOpenQuestionIndex] = X.useState<number | null>(null);

  return (
    <div className="space-y-2 my-6 pt-2 border-t border-gray-200">
      {props.questions.map((question, index) => (
        question.articleLink ? (
          <Resource
            key={index}
            title={question.question}
            url={question.articleLink}
          />
        ) : (
          <Question
            key={index}
            title={question.question}
            answer={question.response}
            isOpen={openQuestionIndex === index}
            setIsOpen={isOpen => {
              if (isOpen && openQuestionIndex !== index) {
                setOpenQuestionIndex(index);
                return;
              }

              if (!isOpen && openQuestionIndex === index) {
                setOpenQuestionIndex(null);
                return;
              }
            }}
          />
        )
      ))}
    </div>
  );
}


function Resource(props: {
  title: string;
  url: string;
}) {
  return (
    <div className="border-b border-gray-200">
      <X.ExternalLink
        href={props.url}
        className={X.clsx(
          "flex justify-between items-center w-full text-left cursor-pointer",
          "pt-2 pb-4 xl:pt-4 xl:pb-6 gap-4 hover:text-black/50"
        )}
      >
        {props.title &&
          <ReactMarkdown components={{
            p: ({ ...props }) => <p {...props} className="text-[16px] xl:text-[24px] select-none" />,
            h1: ({ ...props }) => <p {...props} className="text-[16px] xl:text-[24px] select-none" />,
            h2: ({ ...props }) => <p {...props} className="text-[16px] xl:text-[24px] select-none" />,
            h3: ({ ...props }) => <p {...props} className="text-[16px] xl:text-[24px] select-none" />,
            h4: ({ ...props }) => <p {...props} className="text-[16px] xl:text-[24px] select-none" />
          }}>
            {props.title}
          </ReactMarkdown>
        }
        <div className="flex-1 flex justify-end">
          <X.Icon.MoveRight className="h-[36px]" />
        </div>
      </X.ExternalLink>
    </div>
  );
}


function Question(props: {
  title: React.ReactNode;
  answer: React.ReactNode;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
}) {
  //const [isOpen, setIsOpen] = X.useState(false);
  const iconStyle = "rounded-full p-2 bg-[#efefef]"

  const ref = X.useRef<HTMLDivElement>(null);
  const [height, setHeight] = X.useState("0px");

  X.useEffect([props.isOpen], () => {
    if (!ref.current) return;
    setHeight(props.isOpen ? `${ref.current.scrollHeight}px` : "0px");
  });

  return (
    <div className="border-b border-gray-200">
      <div
        onClick={() => props.setIsOpen(!props.isOpen)}
        className={X.clsx(
          "flex justify-between items-center w-full text-left cursor-pointer",
          "pt-2 pb-4 xl:pt-4 xl:pb-6 gap-4 hover:text-black/50"
        )}
      >
        <p className="text-[16px] xl:text-[24px] select-none">{props.title}</p>

        <div className="flex-1 flex justify-end">
          {props.isOpen ? (
            <X.Icon.Minus size={36} className={iconStyle} />
          ) : (
            <X.Icon.Plus size={36} className={iconStyle} />
          )}
        </div>
      </div>

      <div
        ref={ref}
        style={{ height }}
        className={X.clsx(
          "overflow-hidden transition-[height] duration-300 ease-in-out",
          "QuestionAnswer",
        )}
      >
        <div className={X.clsx("mt-2 pb-6")}>
          {typeof props.answer === "string"
            ? <ReactMarkdown>{props.answer}</ReactMarkdown>
            : props.answer}
        </div>
      </div>

    </div>
  );
}
